/* @flow */

import type { QuoteItem } from "shop-state/types";

import React, { useState, useContext, useEffect } from "react";
import cn from "classnames";
import { Link, withRouter } from "react-router-dom";
import { useTranslate } from "@awardit/react-use-translate";
import { AnalyticsContext } from "@crossroads/analytics";
import QtyPicker from "components/QtyPicker";
import { QuoteData } from "data";
import CustomerServiceLink from "components/CheckoutView/CustomerServiceLink";
import { useSendMessage, useData } from "crustate/react";
import { removeQuoteItem, updateQuoteItemQty } from "@crossroads/shop-state/quote";
import { Dialogue, Tooltip, Button, ButtonLink } from "@crossroads/ui-components";
import { isPointsOnly, pointsPriceByID } from "helpers/points";
import Container from "components/CheckoutView/Container";
import CartSummary from "components/CartSummary";
import { canAffordOrder } from "helpers/utils";
import useCustomer from "helpers/use-customer";
import useFormat from "helpers/use-format";
import CloseIcon from "icons/close-small.svg";
import InfoIcon from "icons/info.svg";
import PriceSplit from "components/PriceSplit";

import styles from "./styles.scss";

const Cart = () => {
  const t = useTranslate();
  const sendMessage = useSendMessage();
  const gaContext = useContext(AnalyticsContext);
  const quote = useData(QuoteData);
  const { formatPoints } = useFormat();
  const [open, setOpen] = useState(true);
  const [activeItem, setActiveItem] = useState<QuoteItem | null>(null);
  const [confirmDialogueOpen, setConfirmDialogueOpen] = useState(false);
  const { customer } = useCustomer();

  const sendGAEvent = (product, quantity, type) => {
    gaContext.registerModifyCart({
      sku: product.sku,
      name: product.name,
      price: product.price,
      qty: quantity,
      attributes: {
        manufacturer: product.attributes.manufacturer,
      },
      categories: product.categories,
    }, type, product.price.incVat);
  };

  useEffect(() => {
    if (quote.data) {
      gaContext.viewedCart(quote.data);
    }
  }, []);

  if (quote.state === "LOADING" || !customer) {
    return null;
  }

  const remove = (item: QuoteItem) => {
    sendGAEvent(item.product, 1, "remove_from_cart");
    sendMessage(removeQuoteItem(item.itemBuyRequest));
  };

  const confirmRemove = (item: QuoteItem) => {
    setConfirmDialogueOpen(true);
    setActiveItem(item);
  };

  const processingItem = typeof quote.processingItem !== "undefined" ? quote.processingItem : null;

  if (!quote.data) {
    return null;
  }

  const { items } = quote.data;

  const accrual = items.reduce((a, c) => {
    const accrual = c.product.type === "configurable" ? c.configOption?.product.attributes.accrual : c.product.attributes.accrual;

    return Number.isNaN(accrual) ? a : a + ((accrual || 0) * c.qty);
  }, 0);

  const canAfford = canAffordOrder(quote.data, customer);

  return (
    <Container
      right={
        <div>
          <CartSummary open={open} setOpen={setOpen}>
            <div className={styles.submitButtonContainer}>
              {!canAfford &&
                <p className={styles.cannotAfford}>{t("CART.TO_CHECKOUT_CANT_AFFORD")}</p>
              }
              {canAfford ?
                <ButtonLink
                  className={styles.submitButton}
                  variant="primary"
                  to="/checkout/1"
                >
                  {t("CART.TO_CHECKOUT")}
                </ButtonLink> :
                <Button
                  disabled
                  className={styles.submitButton}
                  variant="primary"
                >
                  {t("CART.TO_CHECKOUT")}
                </Button>
              }
            </div>

            <div className={styles.accrualInfo}>
              {accrual > 0 &&
                <div>
                  <Tooltip
                    className={styles.tooltip}
                    innerClassName={styles.accrualTooltip}
                    direction="top"
                    title={t("CART.ACCRUAL_TOOLTIP_TITLE")}
                    text={t("CART.ACCRUAL_TOOLTIP_DESCRIPTION")}
                    width={400}
                  >
                    <InfoIcon aria-label={t("ICONS.INFO")} />
                  </Tooltip>

                  <span>{t("CART.YOULL_EARN", { points: formatPoints(accrual) })}</span>
                </div>
              }
              <p>{t("OR")}&nbsp;<Link to="/">{t("CART.CONTINUE_SHOPPING")}</Link></p>
            </div>
          </CartSummary>

          <CustomerServiceLink />
        </div>
      }
    >
      {confirmDialogueOpen &&
      <Dialogue
        className={styles.confirmDialogue}
        open={confirmDialogueOpen}
        setOpen={() => setConfirmDialogueOpen(false)}
        title={t("CART.REMOVE_PRODUCT")}
        closeIcon={
          <CloseIcon title={t("ICONS.CROSS")} />
        }
        primaryAction={t("CART.REMOVE_APPROVE")}
        secondaryAction={t("DIALOGUE.CANCEL")}
        onPrimaryAction={() => {
          if (activeItem) {
            setConfirmDialogueOpen(false);
            remove(activeItem);
          }
        }}
        onSecondaryAction={() => setConfirmDialogueOpen(false)}
      >
        {t("CART.CONFIRM_TEXT", {
          itemName: `${activeItem?.product.name || ""}, ${activeItem?.product.attributes.manufacturer || ""}`,
        })}
      </Dialogue>}
      <div className={styles.block}>
        {items.map(x => {
          const product = x.configOption ? {
            ...x.product,
            ...x.configOption.product,
            categories: x.product.categories,
          } : x.product;

          const { manufacturer } = product.attributes;

          const pointPayment = pointsPriceByID(x.availablePointPayments, "sas_extrapoints");

          if (!pointPayment) {
            return null;
          }

          return (
            <div
              key={x.itemBuyRequest}
              className={
                cn(styles.item, {
                  [styles.processing]: processingItem === x.itemBuyRequest,
                  [styles.disabled]: quote.state === "UPDATING_ITEM",
                })}
            >
              <div className={styles.left}>
                <div style={{ display: "flex", position: "static" }}>
                  <img
                    className={styles.image}
                    alt={product.name}
                    src={product.attributes.image?.x1}
                  />
                  <div className={styles.info}>
                    <div>
                      <Link
                        to={{
                          pathname: product.url,
                          state: { hint: {
                            type: "product",
                            product,
                            image: product.attributes.image?.x1,
                          } },
                        }}
                        className={styles.name}
                      >
                        {product.name}
                      </Link>

                      <Link
                        to={{
                          pathname: `/brand/${encodeURIComponent(manufacturer)}`,
                          state: { hint: { type: "category", category: { name: manufacturer } } },
                        }}
                        className={styles.brand}
                      >
                        {manufacturer}
                      </Link>

                      {x.bundleOptions &&
                        <ul className={styles.bundleOptions}>
                          {x.bundleOptions.map(o => o.products.map(p =>
                            <li key={o.title}>{p.product.name}</li>
                          ))}
                        </ul>
                      }
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.right}>
                <QtyPicker
                  className={styles.qtyPicker}
                  value={x.qty}
                  min={0}
                  setValue={(v: number) => {
                    const type = v > x.qty ? "add_to_cart" : "remove_from_cart";

                    if (v > 0) {
                      sendMessage(updateQuoteItemQty(x.itemBuyRequest, v));
                      sendGAEvent(product, Math.abs(v - x.qty), type);
                    }
                    else {
                      confirmRemove(x);
                    }
                  }} />
                <div className={styles.priceWrapper}>
                  <PriceSplit className={styles.price} pointsPrice={pointPayment} />
                  {isPointsOnly(pointPayment) &&
                    <p className={styles.pointsOnly}>
                      <span />
                      <span>{t("PRODUCT.POINTS_ONLY")}</span>
                    </p>
                  }
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Container>
  );
};

export default withRouter(Cart);
